import { useTranslation } from "react-i18next";

import { Globe, Signal } from "@/assets/icons/ds";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { CountrylistDialog } from "@/components/Portal/DeviceManagement/Roaming/AddRoamingOption/CountryList/CountryList";
import { RoamingUnlimited } from "@/components/Portal/DeviceManagement/Roaming/RoamingUnlimited/RoamingUnlimited";
import { RoamingUsage } from "@/components/Portal/DeviceManagement/Roaming/ShowRoamingOptions/RoamingUsage/RoamingUsage";
import { useBookedRoamingOptionDisplay } from "@/components/Portal/DeviceManagement/Roaming/ShowRoamingOptions/useBookedRoamingOptionDisplay";
import { RoamingInfo, SimCardDetail } from "@/services/model";
import { TOTAL_DAYS_IN_YEAR } from "@/utils/constants";
import { TariffTypes } from "@/utils/deviceUtils";

interface Props {
  roamingInfo: RoamingInfo;
  simDetail: SimCardDetail;
  isFirstOfUsageZone: boolean;
}

/** The "Included in your subscription" card for the mobile-abo included roaming package. */
export const MobileAboRoamingCard = ({
  roamingInfo,
  simDetail,
  isFirstOfUsageZone,
}: Props) => {
  const { t } = useTranslation("portal");
  const {
    shouldDisplayPartialHint,
    roamingCallPackageUsage,
    isRoamingCallsUsageNotAllowed,
    tariffType,
    hasIncludedRoamingCalls,
    shouldTreatAsActive,
  } = useBookedRoamingOptionDisplay(roamingInfo, simDetail, 0);

  return (
    <CardModule
      dataTestid="mobile-abo-roaming-card"
      stickyHeader={false}
      title={t("cockpit.managementTile.roaming.card.mobileAboTitle")}
    >
      <p className="text-body-xs text-foreground-primary">
        {t("cockpit.managementTile.roaming.includedInSubscription")}{" "}
        <CountrylistDialog />
      </p>

      <RoamingUsage
        icon={<Signal className="size-6 shrink-0 text-foreground-primary" />}
        showFlatMobileExplanation={true}
        days={roamingInfo.remainingDays || 0}
        activationDays={roamingInfo.initialDays}
        description="cockpit.managementTile.roaming.showOption.active.remainingData"
        descriptionExtension={t(
          "cockpit.managementTile.roaming.showOption.flatMobileHint.renewalReminder",
        )}
        partialHint={
          shouldDisplayPartialHint && (
            <div className="flex flex-col gap-1">
              <p className="font-semibold">
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileHint.title",
                )}
              </p>
              <p>
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileHint.text",
                )}
              </p>
            </div>
          )
        }
        initial={roamingInfo.initialVolume || 0}
        isFirstOfUsageZone={isFirstOfUsageZone}
        remaining={roamingInfo.remainingVolume || 0}
        consumed={
          (roamingInfo.initialVolume || 0) - (roamingInfo.remainingVolume || 0)
        }
        isIncludedInService={roamingInfo.includedInService}
        roamingStatus={roamingInfo.roamingStatus}
        title={t("cockpit.managementTile.roaming.showOption.active.dataUsage")}
        isYearly={roamingInfo.initialDays === TOTAL_DAYS_IN_YEAR}
        shouldTreatAsActive={shouldTreatAsActive}
        hasIncludedRoamingCalls={hasIncludedRoamingCalls}
        zone={roamingInfo.usageZone}
        isDataUsage={true}
      />

      {tariffType === TariffTypes.flatMobileThree ? (
        <RoamingUnlimited
          showRoamingCallExplanation={true}
          isNotAllowedToUse={isRoamingCallsUsageNotAllowed}
        />
      ) : (
        <RoamingUsage
          icon={<Globe className="size-6 shrink-0 text-foreground-primary" />}
          days={roamingInfo.remainingDays || 0}
          activationDays={roamingInfo.initialDays}
          description="cockpit.managementTile.roaming.showOption.active.remainingMin"
          initial={roamingCallPackageUsage?.maxInclRoamingCalls || 0}
          remaining={
            (roamingCallPackageUsage?.maxInclRoamingCalls || 0) -
            (roamingCallPackageUsage?.roamingCalls || 0)
          }
          consumed={roamingCallPackageUsage?.roamingCalls}
          isIncludedInService={roamingInfo.includedInService}
          roamingStatus={roamingInfo.roamingStatus}
          title={t(
            "cockpit.managementTile.roaming.showOption.active.callsToAndFromAbroad",
          )}
          isFirstOfUsageZone={isFirstOfUsageZone}
          showRoamingCallExplanation={true}
          isNotAllowedToUse={isRoamingCallsUsageNotAllowed}
          shouldTreatAsActive={shouldTreatAsActive}
          hasIncludedRoamingCalls={hasIncludedRoamingCalls}
          isRoamingCallType={true}
        />
      )}
    </CardModule>
  );
};
